import { isToday } from 'date-fns/isToday';
import type React from 'react';

import {
  JobAdType,
  JobStatus,
  PostChannel,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';

import type { IJob } from '../../../../stores/JobStore/Job';
import type { IUser } from '../../../../stores/UserStore';

export interface ActionStatus {
  disabled: boolean;
  disableDescription?: React.ReactNode;
}

export interface JobActionStatus {
  viewActionStatus: ActionStatus;
  editActionStatus: ActionStatus;
  notificationActionStatus: ActionStatus;
  teamAccessActionStatus: ActionStatus;
  shareActionStatus: ActionStatus;
  refreshActionStatus: ActionStatus;
  copyActionStatus: ActionStatus;
  expireActionStatus: ActionStatus;
}

export const ENABLED = { disabled: false };
export const DISABLED = { disabled: true };

const isListingStartDateToday = (job: IJob) => {
  const { listingStartedDate } = job.jobInformation;

  if (listingStartedDate === '') {
    return false;
  }

  return isToday(new Date(listingStartedDate));
};

const isIndirectJob = (job: IJob) =>
  job.jobInformation.postChannel === PostChannel.INDIRECT;

const isDisabledWithMessageCallContactSupport = (user: IUser): boolean =>
  user.showOnCreditHoldMessage;

const isDisabledWithMessageContactAdmin = (user: IUser): boolean =>
  !user.canCreateJobs;

const isDisabledDueToPremiumJobAd = (job: IJob): boolean =>
  job.jobInformation?.adType === JobAdType.PREMIUM;

const viewActionStatus = (job: IJob): ActionStatus => {
  if (
    ![JobStatus.ACTIVE, JobStatus.EXPIRED].includes(job.jobInformation.status)
  ) {
    return DISABLED;
  }

  return ENABLED;
};

interface EditActionStatusOptions {
  allowForIndirectJob: boolean;
  editWhileExpired: boolean;
}

const useEditActionStatus = (
  job: IJob,
  user: IUser,
  options: EditActionStatusOptions = {
    allowForIndirectJob: false,
    editWhileExpired: false,
  },
): ActionStatus => {
  if (!options.allowForIndirectJob && isIndirectJob(job)) {
    return DISABLED;
  }

  if (
    isDisabledWithMessageCallContactSupport(user) &&
    !job.userStore.user.isCsView
  ) {
    return DISABLED;
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status) &&
    !job.userStore.user.isCsView
  ) {
    return DISABLED;
  }

  if (
    !options.editWhileExpired &&
    job.jobInformation.status === JobStatus.EXPIRED
  ) {
    return DISABLED;
  }

  return ENABLED;
};

const useNotificationActionStatus = (job: IJob, user: IUser): ActionStatus =>
  useEditActionStatus(job, user);

const useTeamAccessActionStatus = (job: IJob, user: IUser): ActionStatus =>
  useEditActionStatus(job, user, {
    allowForIndirectJob: true,
    editWhileExpired: true,
  });

const useRefreshActionStatus = (job: IJob, user: IUser): ActionStatus => {
  if (isIndirectJob(job)) {
    return DISABLED;
  }

  if (isDisabledDueToPremiumJobAd(job)) {
    return DISABLED;
  }

  if (isDisabledWithMessageCallContactSupport(user)) {
    return DISABLED;
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return DISABLED;
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status)
  ) {
    return DISABLED;
  }

  if (job.jobInformation.billableApplicants) {
    return DISABLED;
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return DISABLED;
  }

  if (isListingStartDateToday(job)) {
    return DISABLED;
  }

  return ENABLED;
};

const useShareActionStatus = (job: IJob, user: IUser): ActionStatus => {
  if (isDisabledWithMessageCallContactSupport(user)) {
    return DISABLED;
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return DISABLED;
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status)
  ) {
    return DISABLED;
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return DISABLED;
  }

  return ENABLED;
};

const useCopyActionStatus = (job: IJob, user: IUser): ActionStatus => {
  if (isDisabledWithMessageCallContactSupport(user)) {
    return DISABLED;
  }

  if (isDisabledWithMessageContactAdmin(user)) {
    return DISABLED;
  }

  return ENABLED;
};

const useExpireActionStatus = (job: IJob): ActionStatus => {
  if (isIndirectJob(job)) {
    return DISABLED;
  }

  if (
    [
      JobStatus.PENDING,
      JobStatus.PENDING_ACCOUNT_APPROVAL,
      JobStatus.SUSPENDED,
    ].includes(job.jobInformation.status) &&
    !job.userStore.user.isCsView
  ) {
    return DISABLED;
  }

  if (job.jobInformation.status === JobStatus.EXPIRED) {
    return DISABLED;
  }

  return ENABLED;
};

const useJobActionStatus = (job: IJob): JobActionStatus => {
  const { userStore } = job;
  const user = userStore.user;

  return {
    viewActionStatus: viewActionStatus(job),
    editActionStatus: useEditActionStatus(job, user),
    notificationActionStatus: useNotificationActionStatus(job, user),
    teamAccessActionStatus: useTeamAccessActionStatus(job, user),
    shareActionStatus: useShareActionStatus(job, user),
    refreshActionStatus: useRefreshActionStatus(job, user),
    copyActionStatus: useCopyActionStatus(job, user),
    expireActionStatus: useExpireActionStatus(job),
  };
};

export default useJobActionStatus;
